import Datum from '@/interfaces/Datum';
import _ from 'lodash';

export type Component = d3.HierarchyPointNode<Datum>;

type WeightFn = (a: Component) => number;
type CompareFn = (a: Component, b: Component) => number;

export class ComponentSet {
  private readonly _components: Component[];

  constructor(components: Component[]) {
    this._components = components || [];
  }

  public get components(): readonly Component[] {
    return this._components;
  }

  public limitDepth(maxDepth: number) {
    limitDepth(this._components, maxDepth);
  }
  public newLimitDepth(maxDepth: number) {
    _.forEach(this.components, component => {
      newLimitDepth(component, maxDepth);
    });
  }

  public sort(compareFn: CompareFn) {
    sortComponents(this._components, compareFn);
  }

  public add(...items: Component[]) {
    addComponents(this._components, ...items);
  }

  public remove(...items: Component[]) {
    removeComponents(this._components, ...items);
  }

  public join(...items: Component[]): boolean {
    return joinComponents(this._components, ...items);
  }

  public has(item: Component): boolean {
    return includes(this._components, item);
  }
}

export function foldNode(node: Component) {
  if (isUnfolded(node)) {
    node.data._children = node.children; // save children
    node.children = undefined; // collapse children
  } else {
    console.error(`Node-${node.data.id} is already folded:`, node);
  }
}

export function unfoldNode(node: Component) {
  if (isFolded(node)) {
    node.children = node.data._children;
    node.data._children = undefined;
  } else {
    console.error(`Node-${node.data.id} is already unfolded:`, node);
  }
}

export function isFolded(node: Component) {
  return !node.children && node.data._children;
}

export function isUnfolded(node: Component) {
  return !!node.children && !node.data._children;
}

export function limitDepth(components: Component[], maxDepth: number) {
  // Unfold any folded components
  components.filter(isFolded).forEach(unfoldNode);

  // Apply folding/unfolding
  components.forEach(depthPivot => {
    depthPivot.each(foldingPivot => {
      // The traversal starts from the note itself.
      if (depthPivot === foldingPivot) { return; } // skip

      const mustBeFolded = maxDepth <= foldingPivot.depth - depthPivot.depth;

      if (mustBeFolded && isUnfolded(foldingPivot)) {
        foldingPivot.each(n => {
          // The traversal starts from the note itself.
          if (foldingPivot === n) { return; } // skip
          n.data.source = foldingPivot;
        });
        foldNode(foldingPivot);
      } else if (!mustBeFolded && isFolded(foldingPivot)) {
        unfoldNode(foldingPivot);
        foldingPivot.each(n => {
          // The traversal starts from the note itself.
          if (foldingPivot === n) { return; } // skip
          n.data.source = foldingPivot;
        });
      }

    });
  });
}

// TODO interaction-v2에 있는 함수들을 가져왔는데, 정비가 필요할 듯
export function newLimitDepth(c: d3.HierarchyPointNode<Datum>, maxDepth: number) {
  if (0 < maxDepth && folded(c)) { unfold(c); }

  // 상위의 애들도 fold가 되어야 한다.
  let parent = c.parent;
  while (parent) {
    if (folded(parent)) { unfold(parent); }
    parent = parent.parent;
  }

  c.each(d => {
    // The traversal starts from the node itself.
    if (c === d) { return; } // skip

    const mustBeFolded = maxDepth <= d.depth - c.depth;

    if (mustBeFolded && unfolded(d)) {
      setSource(d);
      fold(d);
    }
    if (!mustBeFolded && folded(d)) {
      unfold(d);
      setSource(d);
    }
  });

  if (0 === maxDepth && unfolded(c)) { fold(c); }

  function setSource(d: d3.HierarchyPointNode<Datum>) {
    d.each(n => {
      // The traversal starts from the node itself.
      if (d === n) { return; } // skip
      // n.data.source!.set(n, d);
      n.data.source = d;
    });
  }
}
export function folded(node: d3.HierarchyPointNode<Datum>) {
  return !node.children && isInternal(node);
}
export function unfolded(node: d3.HierarchyPointNode<Datum>) {
  return node.children && isInternal(node);
}
export function fold(c: d3.HierarchyPointNode<Datum>) {
  if (unfolded(c)) {
    setSavedChildren(c);
    c.children = undefined; // collapse children
  } else {
    console.error(c, `is already folded.`);
  }
}
export function unfold(c: d3.HierarchyPointNode<Datum>) {
  if (folded(c)) {
    c.children = getSavedChildren(c);
    deleteSavedChildren(c);
  } else {
    console.error(c, `is already unfolded.`);
  }
}
export function setSavedChildren(c: d3.HierarchyPointNode<Datum>) {
  c.data._children = c.children!;
}
export function getSavedChildren(c: d3.HierarchyPointNode<Datum>) {
  return c.data._children!;
}
export function deleteSavedChildren(c: d3.HierarchyPointNode<Datum>) {
  return c.data._children! = undefined;
}
export function isInternal(c: d3.HierarchyPointNode<Datum>) {
  return !!c.data.children;
}



export function findComponents(
  root: Component,
  compareFn: (a: Component, b: Component) => number,
  conditionFn: (nodes: Component[]) => boolean) {
  const cs = [root];
  while (conditionFn(cs) && cs.some(c => !!c.children)) {
    const last = cs.pop()!;
    const children = last.children;
    if (!!children) {
      cs.push(...children);
      cs.sort(compareFn); // the last one will be the heaviest one.
    } else {
      cs.unshift(last); // until the children is defined, those with no children go to the first.
    }
  }
  return cs.sort(compareFn); // the last one will be the heaviest one.
}

export function sortComponents(
  components: Component[],
  compareFn: CompareFn
) {
  components.sort(compareFn);
}

export function addComponents(
  components: Component[],
  ...items: Component[]
) {
  if (!items.length) { throw new Error('No components are provided.'); }
  items.forEach(addComponent);

  function addComponent(
    item: Component
  ) {
    const start = components.indexOf(item);
    if (start >= 0) { throw new Error('The component already exists.'); }
    components.push(item);
  }
}

export function removeComponents(
  components: Component[],
  ...items: Component[]
) {
  if (!items.length) { throw new Error('No components are provided.'); }
  items.forEach(removeComponent);

  function removeComponent(
    item: Component
  ) {
    const start = components.indexOf(item);
    if (start < 0) { throw new Error('There is no such component.'); }
    components.splice(start, 1);
  }
}

export function joinComponents(
  components: Component[],
  ...items: Component[]
) {
  if (components.length === items.length) {
    const a = components.map(n => n.data.id!).sort();
    const b = items.map(n => n.data.id!).sort();
    if (a.join() === b.join()) {
      console.warn('joinComponents:', 'Trying to join two same arrays. Ignored.');
      return false;
    }
  }
  removeComponents(components, ...components.filter(n => !items.includes(n)));
  addComponents(components, ...items.filter(n => !components.includes(n)));
  return true;
}

export function getWard(node: Component) {
  return node.data.ward;
}

export function getSse(node: Component) {
  return node.data.sse;
}

export function getMse(node: Component) {
  return node.data.mse;
}

export function getRmse(node: Component) {
  return node.data.rmse;
}

export function getCompareFn(weightFn: WeightFn): CompareFn {
  return (a: Component, b: Component) => weightFn(a) - weightFn(b);
}

export function compareWard(a: Component, b: Component) {
  return getCompareFn(getWard)(a, b);
}

export function compareSse(a: Component, b: Component) {
  return getCompareFn(getSse)(a, b);
}

export function compareMse(a: Component, b: Component) {
  return getCompareFn(getMse)(a, b);
}

export function compareRmse(a: Component, b: Component) {
  return getCompareFn(getRmse)(a, b);
}

export function getMaximumWard(
  components: readonly Component[]
) {
  return getMaximumWeight(components, getWard);
}

export function getMaximumWeight(
  components: readonly Component[],
  weightFn: WeightFn
): number {
  return weightFn(getHeaviestComponent(components));
}

export function getHeaviestComponent(
  components: readonly Component[]
): Component {
  return components.slice(-1)[0];
}

export function includes(
  components: readonly Component[],
  item: Component
): boolean {
  return components.includes(item);
}

export function hasComponent(
  components: readonly Component[],
  node: Component
): boolean {
  return node.descendants().some(item => includes(components, item));
}

export function getSibling(component: Component) {
  if (!component.parent || !component.parent.children) {
    throw new Error('Given node has no sibiling.');
  }
  return component.parent.children.filter(child => child !== component).slice(-1)[0];
}
